import { createAsyncThunk } from '@reduxjs/toolkit';
import { Moment, MomentType, FormField } from './slice';
import { MOCK_FORM_FIELDS as FORM_FIELD_DEFINITIONS } from './formFields';
import { getCookie } from '../../../../utils/helpers';

interface OptionType {
  id: string;
  name: string;
}
// Mock data for local development
const MOCK_MOMENT_TYPES: OptionType[] = [
  {
    id: 'release-with-marketing',
    name: 'Release w/ Marketing'
  },
  {
    id: 'release-without-marketing',
    name: 'Release w/o Marketing'
  },
  {
    id: 'marketing-moment',
    name: 'Marketing Moment'
  },
  {
    id: 'offer-price-program',
    name: 'Offer, Price Change or Program (Deprecated)'
  },
  {
    id: 'policy-change',
    name: 'Policy Change'
  },
  {
    id: 'key-date',
    name: 'Key Date'
  },
  {
    id: 'event',
    name: 'Event'
  },
  {
    id: 'Price Changes and Offers',
    name: 'Price Changes and Offers'
  }
];

const MOCK_TYPE_OF_RELEASE_MOMENT: OptionType[] = [
  { id: 'release', name: 'Release' },
  { id: 'moment', name: 'Moment' }
];

// Mock data for Beta or GA options
const MOCK_BETA_OR_GA: OptionType[] = [
  { id: 'internal beta', name: 'Internal Beta' },
  { id: 'private beta', name: 'Private Beta' },
  { id: 'public beta', name: 'Public Beta' },
  { id: 'ga-existing', name: 'GA - Existing' },
  { id: 'ga-new-10', name: 'GA - New 1.0' },
  { id: 'ga-new-10', name: 'GA - New 1.0' }
];

// Mock data for product groups
// Define a generic interface for all option types

// Use the generic type for all mock data
const MOCK_TIERS: OptionType[] = [
  { id: '1', name: 'Tier 1' },
  { id: '2', name: 'Tier 2' },
  { id: '3', name: 'Tier 3' }
];

const MOCK_STATUS: OptionType[] = [
  { id: '1', name: 'Draft' },
  { id: '2', name: 'Published' },
  { id: '3', name: 'Archived' }
];

// Mock data for product groups
const MOCK_PRODUCT_GROUPS: OptionType[] = [
  { id: 'creative-cloud', name: 'Creative Cloud' },
  { id: 'document-cloud', name: 'Document Cloud' },
  { id: 'experience-cloud', name: 'Experience Cloud' }
];

// Mock data for release phases
const MOCK_RELEASE_PHASES: OptionType[] = [
  { id: 'max', name: 'MAX' },
  { id: 'summit', name: 'Summit' },
  { id: 'none', name: 'None' }
];

// Mock data for releases
const MOCK_PART_OF_EVENTS: OptionType[] = [
  { id: 'beta', name: 'Beta' },
  { id: 'ga', name: 'GA' }
];

// Mock data for categories by product group
const MOCK_CATEGORIES_BY_PRODUCT_GROUP: Record<string, OptionType[]> = {
  'creative-cloud': [
    { id: 'design', name: 'Design' },
    { id: 'video', name: 'Video' },
    { id: 'photography', name: 'Photography' },
    { id: 'web', name: 'Web' }
  ],
  'document-cloud': [
    { id: 'pdf', name: 'PDF Services' },
    { id: 'sign', name: 'Adobe Sign' },
    { id: 'scan', name: 'Adobe Scan' }
  ],
  'experience-cloud': [
    { id: 'analytics', name: 'Analytics' },
    { id: 'audience', name: 'Audience Manager' },
    { id: 'campaign', name: 'Campaign' },
    { id: 'commerce', name: 'Commerce' }
  ]
};

// Updated structure for form fields organized by sections
interface FormSection {
  heading: string;
  subheading?: string;
  columns?: number;
  required?: boolean;
  fields: FormField[];
}

interface FormConfig {
  sections: FormSection[];
}

// Update the FormField interface in the slice import
declare module './slice' {
  interface FormField {
    id: string;
    name: string;
    label: string;
    type: string;
    required: boolean;
    options?: { value: string; label: string }[];
    section?: string;
    sectionConfig?: {
      columns: number;
      required?: boolean;
      subheading?: string;
      toolTipMessage?: string;
    };
    api?: string;
    dependsOn?: string;
    dependents?: string[];
    actions?: string[];
    minItems?: number;
    maxItems?: number;
    itemTemplate?: FormField;
    // Optional character limit for rich text fields. null/undefined => unlimited
    charLimit?: number | null;
    disableNA?: boolean;
  }
}

// Use the imported form fields instead of the local definition
const MOCK_FORM_FIELDS = FORM_FIELD_DEFINITIONS;

// Export the flattened form fields function and other utility functions
export const getFlattenedFormFields = (momentTypeId: string): FormField[] => {
  // Rest of the function remains the same...

  // This function is just calling another function with our imported form fields
  return flattenFormFields(momentTypeId);
};

// Function to flatten form fields from sections
export const flattenFormFields = (momentTypeId: string): FormField[] => {
  const formConfig = MOCK_FORM_FIELDS[momentTypeId];
  if (!formConfig) {
    return [];
  }

  const fields: FormField[] = [];
  formConfig.sections.forEach(section => {
    section.fields.forEach(field => {
      // Add the section info to the field
      const fieldWithSection: FormField = {
        ...field,
        section: section.heading,
        sectionConfig: {
          columns: section.columns || 1,
          subheading: section.subheading
        }
      };
      fields.push(fieldWithSection);
    });
  });

  return fields;
};

// API configuration
// Set this to true to use mock data, false to use real API
// In a real app, you might use environment variables:
const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true';

// Optional: Add delay to simulate network latency (in ms)
// Keep this value reasonable (500-2000ms) to avoid app crashes
const MOCK_API_DELAY = 1000;

// Simulated network error rate (0.0 - 1.0)
// Set to 0 to disable errors, 0.1 for 10% error rate, etc.
const MOCK_ERROR_RATE = 0;

// Helper function to safely delay with timeout and error handling
const mockApiCall = <T>(
  data: T,
  delay: number = MOCK_API_DELAY
): Promise<T> => {
  return new Promise((resolve, reject) => {
    // Simulate random network errors
    if (Math.random() < MOCK_ERROR_RATE) {
      setTimeout(() => {
        reject(new Error('Network error - simulated random failure'));
      }, delay / 2); // Fail faster than success for better UX
      return;
    }

    // Normal success path
    const timeoutId = setTimeout(() => {
      resolve(data);
    }, delay);

    // Safety cleanup in case component unmounts during request
    // This is a simplified version - in a real app, you'd want to use
    // a proper cancellation token or AbortController
    return () => clearTimeout(timeoutId);
  });
};

// Add this at the top after imports
const API_BASE_URL =
  process.env.REACT_APP_API_V2_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/product-roadmap/api/v2'
    : '/product-roadmap/api/v2');

// Dynamic registry of API endpoints and mock data for dropdowns
export const API_ENDPOINTS: Record<
  string,
  {
    mockData: any[] | ((params?: any) => any[]);
    endpoint: string;
    dependsOn?: string | string[];
    responseMapper?: (data: any[]) => any[];
  }
> = {
  // Built-in endpoints
  momentTypes: {
    mockData: MOCK_MOMENT_TYPES,
    endpoint: '/api/moment-types',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  typeOfReleaseMoment: {
    mockData: MOCK_TYPE_OF_RELEASE_MOMENT,
    endpoint: '/api/type-of-release-moment',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  betaOrGA: {
    mockData: MOCK_BETA_OR_GA,
    endpoint: '/api/beta-or-ga',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  productGroups: {
    mockData: MOCK_PRODUCT_GROUPS,
    endpoint: `${API_BASE_URL}/product-groups`,
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  categories: {
    mockData: (params: any) =>
      MOCK_CATEGORIES_BY_PRODUCT_GROUP[params.productGroups] || [],
    endpoint: `${API_BASE_URL}/categories?productGroups={productGroups}`,
    dependsOn: 'productGroups',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  tier: {
    mockData: MOCK_TIERS,
    endpoint: '/api/tiers',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  status: {
    mockData: MOCK_STATUS,
    endpoint: '/api/statuses',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  releasePhases: {
    mockData: MOCK_RELEASE_PHASES,
    endpoint: '/api/release-phases',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  partOfEvents: {
    mockData: MOCK_PART_OF_EVENTS,
    endpoint: '/api/part-of-events',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  marketingStrategies: {
    mockData: [
      { id: 'targeted', name: 'Targeted Campaign' },
      { id: 'global', name: 'Global Launch' },
      { id: 'phased', name: 'Phased Rollout' },
      { id: 'regional', name: 'Regional Focus' }
    ],
    endpoint: '/api/marketing-strategies',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  tags: {
    mockData: [
      { id: 'feature', name: 'Feature' },
      { id: 'enhancement', name: 'Enhancement' },
      { id: 'bug-fix', name: 'Bug Fix' },
      { id: 'security', name: 'Security' },
      { id: 'performance', name: 'Performance' },
      { id: 'api', name: 'API' },
      { id: 'ui', name: 'UI/UX' }
    ],
    endpoint: '/api/tags',
    responseMapper: (data: any[]) =>
      data.map(item => ({ id: item.id, name: item.name }))
  },
  // MVP Status - Fetches from form template API (product-roadmap/api/v2/templates/rsd/form)
  mvpStatus: {
    mockData: [], // No mock data - fetched from form template API
    endpoint: `${API_BASE_URL}/templates/rsd/form/field-options/mvpStatus`,
    responseMapper: (data: any[]) =>
      data.map(item => ({
        id: item.value || item.id,
        name: item.label || item.name
      }))
  }
  // Add new endpoints here without modifying component code
};

// Cache for the entire form template response (single API call shared across all fields)
let formTemplateCache: any = null;
let formTemplateFetchPromise: Promise<any> | null = null;

// Cache for extracted field options (fast lookup after template is loaded)
const formTemplateFieldOptionsCache = new Map<string, any[]>();

// Helper function to fetch and cache the form template (ONE API call for all fields)
const fetchFormTemplateCached = async (): Promise<any> => {
  // If already cached, return immediately
  if (formTemplateCache) {
    return formTemplateCache;
  }

  // If fetch is already in progress, wait for it
  if (formTemplateFetchPromise) {
    return formTemplateFetchPromise;
  }

  // Start new fetch
  formTemplateFetchPromise = fetch(`${API_BASE_URL}/templates/rsd/form`, {
    headers: getAuthHeaders()
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch form template');
      }
      return response.json();
    })
    .then(data => {
      formTemplateCache = data;
      formTemplateFetchPromise = null;
      return data;
    })
    .catch(error => {
      formTemplateFetchPromise = null;
      console.error('❌ Error fetching form template:', error);
      throw error;
    });

  return formTemplateFetchPromise;
};

// Helper function to extract field options from form template
const extractFieldOptionsFromFormTemplate = async (
  fieldId: string
): Promise<any[]> => {
  // Check if options are already extracted and cached
  if (formTemplateFieldOptionsCache.has(fieldId)) {
    return formTemplateFieldOptionsCache.get(fieldId) || [];
  }

  try {
    // Fetch the form template (uses cache after first call)
    const allForms = await fetchFormTemplateCached();

    // Extract field options from any moment type (they should all have the same options)
    const formTypes = allForms[0]?.types || {};
    for (const momentType of Object.keys(formTypes)) {
      const sections = formTypes[momentType]?.sections || [];
      for (const section of sections) {
        const field = section.fields?.find(
          (f: any) => f.id === fieldId || f.name === fieldId
        );
        if (field && field.options && Array.isArray(field.options)) {
          const options = field.options.map((opt: any) => ({
            id: opt.value || opt.id,
            name: opt.label || opt.name
          }));
          // Cache the extracted options for instant future access
          formTemplateFieldOptionsCache.set(fieldId, options);
          return options;
        }
      }
    }
    console.warn(`⚠️ No ${fieldId} options found in form template`);
    return [];
  } catch (error) {
    console.error(`❌ Error extracting ${fieldId} from form template:`, error);
    return [];
  }
};

// Generic function to fetch dropdown options by type
export const fetchDropdownOptions = async (
  fieldType: string,
  dependencies?: Record<string, any>
): Promise<any[]> => {
  if (fieldType === 'momentTypes') {
    if (USE_MOCK_API) {
      return MOCK_MOMENT_TYPES;
    }
    return api.fetchMomentTypes();
  }

  // Special handling for fields that come from form template
  if (fieldType === 'mvpStatus') {
    return extractFieldOptionsFromFormTemplate('mvpStatus');
  }

  if (fieldType === 'goals' || fieldType === 'goal') {
    return extractFieldOptionsFromFormTemplate('goal');
  }

  if (fieldType === 'tier') {
    return extractFieldOptionsFromFormTemplate('tier');
  }

  // Check if we have a registered endpoint for this field type
  const endpointConfig = API_ENDPOINTS[fieldType];

  if (!endpointConfig) {
    return [];
  }

  // Check if this endpoint has dependencies and if they're met
  if (endpointConfig.dependsOn && dependencies) {
    // Handle both string and array dependencies
    const dependsOnArray = Array.isArray(endpointConfig.dependsOn)
      ? endpointConfig.dependsOn
      : [endpointConfig.dependsOn];

    // Check if all dependencies are met
    const missingDependency = dependsOnArray.some(dep => !dependencies[dep]);
    if (missingDependency) {
      return [];
    }
  }

  if (USE_MOCK_API) {
    try {
      // Handle function or static mock data
      let mockData;
      if (typeof endpointConfig.mockData === 'function') {
        mockData = endpointConfig.mockData(dependencies || {});
      } else {
        mockData = endpointConfig.mockData;
      }

      // Apply the response mapper
      const mappedData = endpointConfig.responseMapper
        ? endpointConfig.responseMapper(mockData)
        : mockData;

      return await mockApiCall([...mappedData]);
    } catch (error) {
      throw error;
    }
  }

  // Real API call for production
  try {
    // Process the endpoint URL with any parameter substitutions
    let url = endpointConfig.endpoint;

    if (dependencies) {
      // Replace all placeholders in URL template
      Object.entries(dependencies).forEach(([key, value]) => {
        const placeholder = `{${key}}`;
        if (url.includes(placeholder)) {
          url = url.replace(placeholder, encodeURIComponent(value));
        }
      });
    }

    // Make the actual API call
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${fieldType}: ${response.status}`);
    }

    const data = await response.json();
    return endpointConfig.responseMapper
      ? endpointConfig.responseMapper(data)
      : data;
  } catch (error) {
    throw error;
  }
};

// Add this helper function before the API methods
const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

// API methods
export const api = {
  // Fetch moment types
  fetchMomentTypes: async (): Promise<OptionType[]> => {
    if (USE_MOCK_API) {
      return fetchDropdownOptions('momentTypes');
    }
    // Real API call: fetch all forms and extract types
    try {
      const response = await fetch(`${API_BASE_URL}/templates/rsd/form/types`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch all forms: ${response.status}`);
      }
      const apiTypes = await response.json();
      // The new API returns an array of { label, value }
      const types = Array.isArray(apiTypes)
        ? apiTypes.map((item: any) => ({ id: item.value, name: item.label }))
        : [];
      return types;
    } catch (error) {
      throw error;
    }
  },

  // Fetch product groups
  fetchProductGroups: async (): Promise<OptionType[]> => {
    return fetchDropdownOptions('productGroups');
  },

  // Fetch categories based on product group
  fetchCategories: async (productGroupId: string): Promise<OptionType[]> => {
    return fetchDropdownOptions('categories', {
      productGroups: productGroupId
    });
  },

  // Fetch form sections for a specific moment type
  fetchFormConfig: async (momentTypeId: string): Promise<FormConfig | null> => {
    if (USE_MOCK_API) {
      try {
        const config = MOCK_FORM_FIELDS[momentTypeId];
        return await mockApiCall(config || null);
      } catch (error) {
        throw error;
      }
    }

    // Real API call: fetch all forms and filter client-side
    try {
      const response = await fetch(`${API_BASE_URL}/templates/rsd/form`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch all forms: ${response.status}`);
      }
      const allForms = await response.json();
      // allForms is expected to be an object keyed by momentTypeId
      const config = allForms[0].types[momentTypeId] || null;
      return config;
    } catch (error) {
      throw error;
    }
  },

  // Fetch form fields for a specific moment type with section information
  fetchFormFields: async (momentTypeId: string): Promise<FormField[]> => {
    if (USE_MOCK_API) {
      try {
        // Get the form fields with section information
        const fields = getFlattenedFormFields(momentTypeId);

        // Enhance fields with additional section properties
        const formConfig = MOCK_FORM_FIELDS[momentTypeId];
        if (formConfig) {
          // Create a map of section configs by section heading
          const sectionConfigMap = new Map();
          formConfig.sections.forEach(section => {
            sectionConfigMap.set(section.heading, {
              columns: section.columns || 1,
              required: (section as any).required,
              subheading: section.subheading
            });
          });

          // Add section config to each field
          const enhancedFields = fields.map(field => {
            const sectionConfig = sectionConfigMap.get(field.section) || {};
            return {
              ...field,
              sectionConfig
            };
          });

          return await mockApiCall([...enhancedFields]);
        }

        return await mockApiCall([...fields]);
      } catch (error) {
        throw error;
      }
    }

    // Real API call: fetch all forms and filter client-side
    try {
      const response = await fetch(`${API_BASE_URL}/templates/rsd/form`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch all forms: ${response.status}`);
      }
      const allForms = await response.json();
      const formConfig = allForms[0].types[momentTypeId];
      if (!formConfig) return [];
      // Flatten fields from sections
      const fields: FormField[] = [];
      formConfig.sections.forEach((section: any) => {
        section.fields.forEach((field: any) => {
          const fieldWithSection: FormField = {
            ...field,
            section: section.heading,
            sectionConfig: {
              columns: section.columns || 1,
              required: section.required,
              subheading: section.subheading
            }
          };
          fields.push(fieldWithSection);
        });
      });
      return fields;
    } catch (error) {
      throw error;
    }
  },

  // Create a moment
  createMoment: async (
    moment: Omit<Moment, 'id' | 'createdAt'>
  ): Promise<Moment> => {
    if (USE_MOCK_API) {
      try {
        const newMoment: Moment = {
          ...moment,
          id: `mock-${Date.now()}`,
          createdAt: new Date().toISOString()
        };
        return await mockApiCall(newMoment);
      } catch (error) {
        throw error;
      }
    }

    // Real API call
    const response = await fetch(`${API_BASE_URL}/moments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(moment)
    });
    if (!response.ok) {
      // Try to extract the error message from the response body
      let errorMessage = `Failed to create moment: ${response.status}`;
      try {
        const errorData = await response.json();
        // Common error response formats
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch (textError) {
          // Keep the default error message
        }
      }
      throw new Error(errorMessage);
    }
    return await response.json();
  },

  // Fetch all form configs (for all moment types)
  fetchAllFormConfigs: async (): Promise<Record<string, any>> => {
    if (USE_MOCK_API) {
      return MOCK_FORM_FIELDS;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/templates/rsd/form`, {
        headers: getAuthHeaders()
      });
      // const response = await fetch(
      //   `${process.env.PUBLIC_URL || ''}/form.json`,
      //   {
      //     headers: getAuthHeaders()
      //   }
      // );
      if (!response.ok) {
        throw new Error(`Failed to fetch all forms: ${response.status}`);
      }
      const allForms = await response.json();
      return allForms[0].types || allForms[0];
    } catch (error) {
      throw error;
    }
  }
};
